import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { parseBowlingCsv, ParsedBowlerScore } from './csvScoreParser';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface Bowler { id: number; name: string; email: string; lane?: number | null; letter?: string | null; league_handicap?: number; set_average?: number | null; computed_average?: number | null; }
interface SEStanding {
  bowler_id: number;
  bowler_name: string;
  best_game: number | null;
  total_score: number | null;
  games_entered: number;
  game_scores: { game: number; score: number | null }[];
}
interface SEResult {
  id: number;
  name: string;
  type: string;
  applies_to: string;
  applies_to_game_numbers: string | null;
  // generic standings (high_game / high_series / custom)
  standings: SEStanding[];
  // survivor / eliminator
  cashers?: any[];
  computed_rounds?: any[];
  // mystery
  mystery_target?: number | null;
  matching_bowlers?: any[];
  // clean_game
  clean_game_winners?: string;
  // side_pots
  pot_results?: any[];
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const MAX_GAMES = 6;
const SCORE_KEY = (bowlerId: number, game: number) => `${bowlerId}-${game}`;

// ─── Type label helper ────────────────────────────────────────────────────────
const TYPE_LABELS: Record<string, string> = {
  bracket: 'Bracket', high_game: 'High Game', survivor: 'Survivor',
  eliminator: 'Eliminator', side_pots: 'Side Pots',
};

interface Props {
  squadId: number;
  squadName: string;
  squadDate: string;
  leagueId: number;
  onClose: () => void;
}

const BookieScoreEntry: React.FC<Props> = ({ squadId, squadName, squadDate, leagueId, onClose }) => {
  const { t } = useTranslation();

  const [bowlers, setBowlers] = useState<Bowler[]>([]);
  const [loadingBowlers, setLoadingBowlers] = useState(true);
  const [savedScores, setSavedScores] = useState<Record<string, number>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, SaveStatus>>({});
  const [activeGame, setActiveGame] = useState(0);
  const [totalGames, setTotalGames] = useState(3); // minimum 3 game tabs
  const [seResults, setSeResults] = useState<SEResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const savingRef = useRef<Record<string, boolean>>({});
  const [laneInputs, setLaneInputs] = useState<Record<number, string>>({});
  const [laneSaveStatus, setLaneSaveStatus] = useState<Record<number, SaveStatus>>({});
  const [posInputs, setPosInputs] = useState<Record<number, string>>({});
  const [posSaveStatus, setPosSaveStatus] = useState<Record<number, SaveStatus>>({});
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvSummary, setCsvSummary] = useState<{ imported: number; skipped: number; warnings: string[] } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Search-to-scroll ─────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const matchedBowlerId = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const fmt = (name: string) => { const i = name.indexOf(' '); return i === -1 ? name : `${name.slice(i + 1)}, ${name.slice(0, i)}`; };
    // If user typed "Last First" (space, no comma), convert to "Last, First" for matching
    const normalized = (q.includes(' ') && !q.includes(','))
      ? q.replace(' ', ', ')
      : q;
    const match = bowlers.find(b =>
      fmt(b.name).toLowerCase().startsWith(normalized) ||
      b.name.toLowerCase().startsWith(q)
    );
    return match?.id ?? null;
  }, [searchQuery, bowlers]);

  useEffect(() => {
    if (matchedBowlerId !== null) {
      rowRefs.current[matchedBowlerId]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [matchedBowlerId]);

  // ── Lane helpers ──────────────────────────────────────────────────────────

  /** Convert stored letter → 1-based position number (1–5) */
  const letterToPos = (letter: string | null | undefined, lane: number | null | undefined): number | null => {
    if (!letter || lane == null) return null;
    const opts = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
    const idx = opts.indexOf(letter);
    return idx === -1 ? null : idx + 1;
  };

  /** Convert 1-based position number → letter for the given lane */
  const posToLetter = (pos: number, lane: number): string | null => {
    const opts = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
    return opts[pos - 1] ?? null;
  };

  const formatName = (name: string) => {
    const idx = name.indexOf(' ');
    if (idx === -1) return name;
    return `${name.slice(idx + 1)}, ${name.slice(0, idx)}`;
  };

  const sortBowlersByLane = (list: Bowler[]) =>
    [...list].sort((a, b) => {
      if (a.lane == null && b.lane == null) return a.name.localeCompare(b.name);
      if (a.lane == null) return 1;
      if (b.lane == null) return -1;
      if (a.lane !== b.lane) return a.lane - b.lane;
      // Same lane — sort by letter (nulls last), then name
      if (a.letter == null && b.letter == null) return a.name.localeCompare(b.name);
      if (a.letter == null) return 1;
      if (b.letter == null) return -1;
      return a.letter.localeCompare(b.letter) || a.name.localeCompare(b.name);
    });

  const saveLane = useCallback((bowlerId: number, value: string) => {
    const lane = parseInt(value, 10);
    if (!value.trim() || isNaN(lane) || lane < 1) return;
    setLaneSaveStatus(prev => ({ ...prev, [bowlerId]: 'saving' }));
    axios.put(
      `${API}/bookie-scores/squads/${squadId}/bowlers/${bowlerId}/lane`,
      { lane },
      { headers: authHeader() }
    ).then(() => {
      const validLetters = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
      setLaneSaveStatus(prev => ({ ...prev, [bowlerId]: 'saved' }));
      setBowlers(prev => {
        const updated = prev.map(b => {
          if (b.id !== bowlerId) return b;
          const letterInvalid = b.letter != null && !validLetters.includes(b.letter);
          return { ...b, lane, letter: letterInvalid ? null : b.letter };
        });
        return sortBowlersByLane(updated);
      });
      // Auto-assign or re-map position after lane change
      setPosInputs(prevPos => {
        const curPos = prevPos[bowlerId] ? parseInt(prevPos[bowlerId], 10) : null;
        if (curPos != null && curPos >= 1 && curPos <= 5) {
          // Already has a position — update the letter for the new lane parity
          const letter = posToLetter(curPos, lane);
          if (letter) {
            axios.put(`${API}/bookie-scores/squads/${squadId}/bowlers/${bowlerId}/letter`,
              { letter }, { headers: authHeader() })
              .then(() => setBowlers(prev => sortBowlersByLane(prev.map(b => b.id === bowlerId ? { ...b, letter } : b))));
          }
          return prevPos;
        } else {
          // Auto-assign the next free position on this lane
          const sameLane = bowlers.filter(b => b.id !== bowlerId && b.lane === lane);
          const usedPos = new Set(sameLane.map(b => letterToPos(b.letter, lane)));
          let nextPos = 1;
          while (usedPos.has(nextPos) && nextPos <= 5) nextPos++;
          if (nextPos <= 5) {
            const letter = posToLetter(nextPos, lane);
            if (letter) {
              axios.put(`${API}/bookie-scores/squads/${squadId}/bowlers/${bowlerId}/letter`,
                { letter }, { headers: authHeader() })
                .then(() => setBowlers(prev => sortBowlersByLane(prev.map(b => b.id === bowlerId ? { ...b, letter } : b))));
              return { ...prevPos, [bowlerId]: String(nextPos) };
            }
          }
          return prevPos;
        }
      });
      setTimeout(() => setLaneSaveStatus(prev => ({ ...prev, [bowlerId]: 'idle' })), 1500);
    }).catch(() => {
      setLaneSaveStatus(prev => ({ ...prev, [bowlerId]: 'error' }));
    });
  }, [squadId, bowlers]);

  const saveLetter = useCallback((bowlerId: number, value: string) => {
    setPosSaveStatus(prev => ({ ...prev, [bowlerId]: 'saving' }));
    axios.put(
      `${API}/bookie-scores/squads/${squadId}/bowlers/${bowlerId}/letter`,
      { letter: value || null },
      { headers: authHeader() }
    ).then(() => {
      setPosSaveStatus(prev => ({ ...prev, [bowlerId]: 'saved' }));
      setBowlers(prev => {
        const updated = prev.map(b => b.id === bowlerId ? { ...b, letter: value || null } : b);
        return sortBowlersByLane(updated);
      });
      setTimeout(() => setPosSaveStatus(prev => ({ ...prev, [bowlerId]: 'idle' })), 1500);
    }).catch(() => {
      setPosSaveStatus(prev => ({ ...prev, [bowlerId]: 'error' }));
    });
  }, [squadId]);

  /** Save a 1-based position number, auto-swapping any colliding bowler on the same lane. */
  const savePosition = useCallback((bowlerId: number, posStr: string) => {
    const pos = parseInt(posStr, 10);
    const bowler = bowlers.find(b => b.id === bowlerId);
    if (!bowler || bowler.lane == null || isNaN(pos) || pos < 1 || pos > 5) return;
    const newLetter = posToLetter(pos, bowler.lane);
    if (!newLetter) return;

    // Swap if another bowler on the same lane already occupies this position
    const collision = bowlers.find(b => b.id !== bowlerId && b.lane === bowler.lane && b.letter === newLetter);
    if (collision && bowler.letter) {
      const oldLetter = bowler.letter;
      const oldPos = letterToPos(oldLetter, bowler.lane);
      axios.put(`${API}/bookie-scores/squads/${squadId}/bowlers/${collision.id}/letter`,
        { letter: oldLetter }, { headers: authHeader() })
        .then(() => {
          setBowlers(prev => sortBowlersByLane(prev.map(b => b.id === collision.id ? { ...b, letter: oldLetter } : b)));
          setPosInputs(prev => ({ ...prev, [collision.id]: String(oldPos ?? '') }));
        });
    }

    saveLetter(bowlerId, newLetter);
    setPosInputs(prev => ({ ...prev, [bowlerId]: String(pos) }));
  }, [squadId, bowlers, saveLetter]);

  // ── Load bowlers + existing scores ────────────────────────────────────────
  const loadData = useCallback(() => {
    setLoadingBowlers(true);
    Promise.all([
      axios.get(`${API}/bookie-scores/squads/${squadId}/scoring-bowlers`, { headers: authHeader() }),
      axios.get(`${API}/bookie-scores/squads/${squadId}/scores`, { headers: authHeader() }),
    ]).then(([bowlersRes, scoresRes]) => {
      const loadedBowlers: Bowler[] = bowlersRes.data;
      setBowlers(sortBowlersByLane(loadedBowlers));

      // Initialise lane + letter inputs from loaded values
      const lanes: Record<number, string> = {};
      const positions: Record<number, string> = {};
      for (const b of loadedBowlers) {
        if (b.lane != null) lanes[b.id] = String(b.lane);
        if (b.letter != null && b.lane != null) {
          const valid = b.lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
          const idx = valid.indexOf(b.letter);
          if (idx !== -1) positions[b.id] = String(idx + 1);
        }
      }
      setLaneInputs(lanes);
      setPosInputs(positions);

      const saved: Record<string, number> = {};
      const inputs: Record<string, string> = {};
      let maxGame = 3;

      for (const s of scoresRes.data) {
        const key = SCORE_KEY(s.bowler_id, s.game_number);
        saved[key] = s.score;
        inputs[key] = String(s.score);
        if (s.game_number > maxGame) maxGame = s.game_number;
      }

      setSavedScores(saved);
      setInputValues(inputs);
      setTotalGames(maxGame);
    }).catch(() => {})
      .finally(() => setLoadingBowlers(false));
  }, [squadId]);

  const loadSEResults = useCallback(() => {
    setLoadingResults(true);
    axios.get(`${API}/bookie-scores/squads/${squadId}/side-event-results`, { headers: authHeader() })
      .then(r => setSeResults(r.data))
      .catch(() => {})
      .finally(() => setLoadingResults(false));
  }, [squadId]);

  useEffect(() => {
    loadData();
    loadSEResults();
  }, [loadData, loadSEResults]);

  // ── Save a single score ───────────────────────────────────────────────────
  const saveScore = useCallback(async (bowlerId: number, gameNumber: number, rawValue: string) => {
    const key = SCORE_KEY(bowlerId, gameNumber);
    const score = parseInt(rawValue, 10);

    if (rawValue === '' || isNaN(score)) return; // blank = no save
    if (score < 0 || score > 300) {
      setSaveStatus(prev => ({ ...prev, [key]: 'error' })); return;
    }
    // Skip if already saved with same value
    if (savedScores[key] === score) return;
    // Skip if already saving
    if (savingRef.current[key]) return;

    savingRef.current[key] = true;
    setSaveStatus(prev => ({ ...prev, [key]: 'saving' }));

    try {
      await axios.post(
        `${API}/bookie-scores/squads/${squadId}/scores`,
        { bowlerId, gameNumber, score },
        { headers: authHeader() }
      );
      setSavedScores(prev => ({ ...prev, [key]: score }));
      setSaveStatus(prev => ({ ...prev, [key]: 'saved' }));
      // Refresh SE results after save
      loadSEResults();
      // Clear 'saved' indicator after 2s
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [key]: 'idle' })), 2000);
    } catch {
      setSaveStatus(prev => ({ ...prev, [key]: 'error' }));
    } finally {
      savingRef.current[key] = false;
    }
  }, [savedScores, squadId, loadSEResults]);

  const handleInput = (bowlerId: number, value: string) => {
    const key = SCORE_KEY(bowlerId, activeGame);
    setInputValues(prev => ({ ...prev, [key]: value }));
    // Clear save status when typing
    setSaveStatus(prev => ({ ...prev, [key]: 'idle' }));
  };

  const handleBlur = (bowlerId: number) => {
    const key = SCORE_KEY(bowlerId, activeGame);
    saveScore(bowlerId, activeGame, inputValues[key] ?? '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, bowlerId: number, bowlerIndex: number) => {
    if (e.key === 'Enter') {
      handleBlur(bowlerId);
      // Move to next bowler
      if (bowlerIndex < bowlers.length - 1) {
        const nextInput = document.getElementById(`score-${bowlers[bowlerIndex + 1].id}`);
        nextInput?.focus();
      }
    }
  };

  const addGame = () => {
    if (totalGames < MAX_GAMES) {
      const next = totalGames + 1;
      setTotalGames(next);
      setActiveGame(next);
    }
  };

  // ── All Games mode handlers ────────────────────────────────────────────────
  const handleAllGamesInput = (bowlerId: number, game: number, value: string) => {
    const key = SCORE_KEY(bowlerId, game);
    setInputValues(prev => ({ ...prev, [key]: value }));
    setSaveStatus(prev => ({ ...prev, [key]: 'idle' }));
  };

  const handleAllGamesBlur = (bowlerId: number, game: number) => {
    saveScore(bowlerId, game, inputValues[SCORE_KEY(bowlerId, game)] ?? '');
  };

  const handleAllGamesKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    bowlerId: number,
    game: number,
    bowlerIndex: number,
  ) => {
    if (e.key === 'Enter') {
      handleAllGamesBlur(bowlerId, game);
      // Enter on last game of a bowler → jump to next bowler's G1
      if (game === totalGames) {
        if (bowlerIndex < bowlers.length - 1) {
          document.getElementById(`score-all-${bowlers[bowlerIndex + 1]!.id}-1`)?.focus();
        }
      } else {
        // Enter mid-row → jump to next game in same bowler
        document.getElementById(`score-all-${bowlerId}-${game + 1}`)?.focus();
      }
    }
  };

  // ── CSV import ────────────────────────────────────────────────────────────
  const applyCsvScores = useCallback(async (entries: ParsedBowlerScore[], warnings: string[]) => {
    setCsvImporting(true);

    // Build lookup: "lane-position" -> bowlerId  (position 1-5 within lane)
    // Derive position from the stored letter: A/B=1, C/D=2, E/F=3, G/H=4, I/J=5
    const letterToPosition = (letter: string, lane: number): number | null => {
      const opts = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
      const idx = opts.indexOf(letter);
      return idx === -1 ? null : idx + 1;
    };
    const bowlerMap = new Map<string, number>();
    for (const b of bowlers) {
      if (b.lane != null && b.letter != null) {
        const pos = letterToPosition(b.letter, b.lane);
        if (pos != null) bowlerMap.set(`${b.lane}-${pos}`, b.id);
      }
    }

    // Collect all (bowlerId, gameNumber, score) pairs to save
    type ScoreSave = { bowlerId: number; gameNumber: number; score: number; key: string };
    let imported = 0;
    let skipped = 0;
    let failed = 0;
    const newInputValues: Record<string, string> = {};
    const newSavedScores: Record<string, number> = {};
    const scoresToSave: ScoreSave[] = [];
    let maxGameInCsv = totalGames;

    for (const entry of entries) {
      const bowlerId = bowlerMap.get(`${entry.lane}-${entry.position}`);

      if (bowlerId == null) {
        skipped++;
        continue;
      }

      let hasGame = false;
      for (let gi = 0; gi < entry.games.length; gi++) {
        const score = entry.games[gi];
        // Skip 0-scores — bowling-alley CSV uses 0 as placeholder (handicap, avg, etc.)
        if (score === 0) continue;

        const gameNumber = gi + 1;
        if (gameNumber > maxGameInCsv) maxGameInCsv = gameNumber;

        const key = SCORE_KEY(bowlerId, gameNumber);
        newInputValues[key] = String(score);
        newSavedScores[key] = score;
        hasGame = true;
        scoresToSave.push({ bowlerId, gameNumber, score, key });
      }

      if (hasGame) imported++;
      else skipped++;
    }

    // Update the UI immediately — scores appear before network round-trips
    setInputValues(prev => ({ ...prev, ...newInputValues }));
    setSavedScores(prev => ({ ...prev, ...newSavedScores }));
    if (maxGameInCsv > totalGames) setTotalGames(maxGameInCsv);

    // Save sequentially — firing all at once causes MySQL deadlocks because every
    // request races to INSERT the same Games row (match_id + game_number).
    for (const { bowlerId, gameNumber, score } of scoresToSave) {
      try {
        await axios.post(
          `${API}/bookie-scores/squads/${squadId}/scores`,
          { bowlerId, gameNumber, score },
          { headers: authHeader() }
        );
      } catch {
        failed++;
      }
    }

    const warnsCopy = [...warnings];
    if (failed > 0) warnsCopy.push(`${failed} score(s) failed to save — please re-enter them manually.`);

    setCsvImporting(false);
    setCsvSummary({ imported, skipped, warnings: warnsCopy });
    loadSEResults();
  }, [bowlers, squadId, totalGames, loadSEResults]);

  const handleCsvFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setCsvSummary({ imported: 0, skipped: 0, warnings: ['Invalid file — please select a .csv file.'] });
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const result = parseBowlingCsv(text);
      applyCsvScores(result.entries, result.warnings);
    };
    reader.readAsText(file);
  }, [applyCsvScores]);

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCsvFile(file);
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString();

  // ── Status icon ───────────────────────────────────────────────────────────
  const StatusIcon = ({ status }: { status: SaveStatus }) => {
    if (status === 'saving') return (
      <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
    );
    if (status === 'saved') return (
      <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    );
    if (status === 'error') return (
      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
    return <div className="w-4 h-4 flex-shrink-0" />;
  };

  // ── Rank badge ────────────────────────────────────────────────────────────
  const RankBadge = ({ rank }: { rank: number }) => {
    const colors = ['bg-yellow-500', 'bg-gray-400', 'bg-amber-600'];
    const color = rank <= 3 ? colors[rank - 1] : 'bg-gray-700';
    return (
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white ${color}`}>
        {rank}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose}
            className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="font-bold text-white text-base leading-tight">
              {t('bookie.scores.title', 'Enter Scores')} — {squadName}
            </h2>
            <p className="text-xs text-gray-400">{fmtDate(squadDate)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Import CSV */}
          <button
            onClick={() => fileInputRef.current?.click()}
            title={t('bookie.scores.importCsvTitle', 'Import scores from a bowling-alley CSV file, or drag & drop it onto the score grid')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-800/60 text-indigo-200 text-xs rounded-lg border border-indigo-600/50 hover:bg-indigo-700/70 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {t('bookie.scores.importCsv', 'Import CSV')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={e => {
              if (e.target.files?.[0]) {
                handleCsvFile(e.target.files[0]);
                e.target.value = ''; // reset so same file can be re-selected
              }
            }}
          />
          {/* Toggle results panel (mobile) */}
          <button
            onClick={() => setShowResults(v => !v)}
            className="sm:hidden px-3 py-1.5 bg-purple-700/60 text-xs text-purple-200 rounded-lg"
          >
            {showResults ? t('bookie.scores.hideResults', 'Hide Results') : t('bookie.scores.showResults', 'Show Results')}
          </button>
        </div>
      </div>

      {/* ── Game Tabs ────────────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 flex items-center gap-1 flex-shrink-0">
        {/* All Games tab */}
        <button onClick={() => setActiveGame(0)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5
            ${activeGame === 0 ? 'border-purple-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
          All Games
          <span className="text-[9px] bg-purple-800/60 text-purple-300 px-1.5 py-0.5 rounded-full">fast</span>
        </button>
        {/* Divider */}
        <span className="w-px h-5 bg-gray-700 mx-1 flex-shrink-0" />
        {Array.from({ length: totalGames }, (_, i) => i + 1).map(g => {
          const hasScores = bowlers.some(b => savedScores[SCORE_KEY(b.id, g)] !== undefined);
          return (
            <button key={g} onClick={() => setActiveGame(g)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5
                ${activeGame === g ? 'border-purple-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
              {t('bookie.scores.game', 'Game')} {g}
              {hasScores && (
                <span className={`w-1.5 h-1.5 rounded-full ${activeGame === g ? 'bg-green-400' : 'bg-green-600'}`} />
              )}
            </button>
          );
        })}
        {totalGames < MAX_GAMES && (
          <button onClick={addGame}
            className="px-3 py-2.5 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('bookie.scores.addGame', 'Add Game')}
          </button>
        )}
      </div>

      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-2 flex-shrink-0">
        <div className="relative max-w-xs">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Find bowler…"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-7 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden flex">

        {/* Score Grid — drag-and-drop target */}
        <div
          className="flex-1 overflow-y-auto p-4 relative"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag-over overlay */}
          {isDragOver && (
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
              style={{ background: 'rgba(79,70,229,0.18)', border: '2px dashed #818cf8', borderRadius: '0.5rem' }}>
              <div className="text-center">
                <div className="text-5xl mb-2">📥</div>
                <p className="text-indigo-200 font-semibold text-lg">Drop CSV to import scores</p>
              </div>
            </div>
          )}
          {loadingBowlers ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : bowlers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-3">🎳</div>
              <p className="text-gray-400">{t('bookie.scores.noBowlers', 'No bowlers have side event entries for this squad yet.')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('bookie.scores.addEntriesFirst', 'Add entries to side events first.')}</p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {bowlers.length} {t('bookie.scores.bowlersEnrolled', 'bowlers enrolled')} ·{' '}
                  {t('bookie.scores.autoSave', 'Scores auto-save on tab/enter')}
                </p>
                <div className="text-xs text-gray-600">
                  {t('bookie.scores.game', 'Game')} {activeGame} / {totalGames}
                </div>
              </div>

              {activeGame === 0 ? (
                /* ── All Games Grid ─────────────────────────────────── */
                <div className="space-y-1.5">
                  {/* Column header */}
                  <div className="grid gap-2 px-3 pb-1"
                    style={{ gridTemplateColumns: `64px 48px 1fr ${Array.from({ length: totalGames }).map(() => '86px').join(' ')} 56px 28px` }}>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Lane</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Pos</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Bowler</span>
                    {Array.from({ length: totalGames }, (_, i) => (
                      <span key={i} className="text-xs text-gray-500 uppercase tracking-wide text-center">G{i + 1}</span>
                    ))}
                    <span className="text-xs text-gray-500 uppercase tracking-wide text-center">Total</span>
                    <span />
                  </div>

                  {bowlers.map((bowler, bowlerIndex) => {
                    const games = Array.from({ length: totalGames }, (_, i) => i + 1);
                    const statuses = games.map(g => saveStatus[SCORE_KEY(bowler.id, g)] ?? 'idle');
                    const rowStatus: SaveStatus =
                      statuses.includes('saving') ? 'saving' :
                      statuses.includes('error') ? 'error' :
                      statuses.includes('saved') ? 'saved' : 'idle';
                    const gameScoreValues = games.map(g => savedScores[SCORE_KEY(bowler.id, g)]);
                    const total = gameScoreValues.filter(s => s !== undefined).reduce((a, b) => a + b!, 0);
                    const hasAny = gameScoreValues.some(s => s !== undefined);

                    return (
                      <div key={bowler.id}
                        ref={el => { rowRefs.current[bowler.id] = el; }}
                        className={`grid gap-2 items-center bg-gray-800 border rounded-lg px-3 py-2 transition-colors
                          ${bowler.id === matchedBowlerId ? 'border-purple-500 ring-1 ring-purple-500/60' : rowStatus === 'saved' ? 'border-green-700/50' : rowStatus === 'error' ? 'border-red-700/50' : 'border-gray-700'}`}
                        style={{ gridTemplateColumns: `64px 72px 1fr ${Array.from({ length: totalGames }).map(() => '86px').join(' ')} 56px 28px` }}>
                        {/* Lane cell */}
                        <input
                          type="number"
                          tabIndex={-1}
                          min={1}
                          placeholder="—"
                          value={laneInputs[bowler.id] ?? ''}
                          onChange={e => setLaneInputs(prev => ({ ...prev, [bowler.id]: e.target.value }))}
                          onBlur={() => saveLane(bowler.id, laneInputs[bowler.id] ?? '')}
                          className={`w-full text-center px-1 py-1.5 rounded-lg text-sm font-bold
                            bg-gray-700 border focus:outline-none focus:border-purple-500 transition-colors
                            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                            ${laneSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                              laneSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                              'border-gray-600 text-white'}`}
                        />
                        {/* Position cell */}
                        <input
                          type="number"
                          tabIndex={-1}
                          min={1}
                          max={5}
                          placeholder="—"
                          disabled={bowler.lane == null}
                          value={posInputs[bowler.id] ?? ''}
                          onChange={e => setPosInputs(prev => ({ ...prev, [bowler.id]: e.target.value }))}
                          onBlur={() => savePosition(bowler.id, posInputs[bowler.id] ?? '')}
                          className={`w-full text-center px-1 py-1.5 rounded-lg text-sm font-bold
                            bg-gray-700 border focus:outline-none transition-colors
                            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                            ${bowler.lane == null ? 'opacity-40 cursor-not-allowed border-gray-700 text-gray-500' :
                              (posInputs[bowler.id] && (parseInt(posInputs[bowler.id]) < 1 || parseInt(posInputs[bowler.id]) > 5)) ? 'border-red-500 text-red-300' :
                              posSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                              posSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                              'border-gray-600 text-white'}`}
                        />
                        <div>
                          <div className="text-white text-sm font-medium truncate">{formatName(bowler.name)}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {(() => {
                              const avg = bowler.computed_average ?? bowler.set_average ?? null;
                              return avg != null ? (
                                <span className="text-[10px] text-blue-400 font-mono">{Number(avg).toFixed(0)} avg</span>
                              ) : null;
                            })()}
                            {(bowler.league_handicap ?? 0) > 0 && (
                              <span className="text-[10px] text-amber-400 font-mono">+{bowler.league_handicap} hdcp</span>
                            )}
                          </div>
                        </div>
                        {games.map(g => {
                          const key = SCORE_KEY(bowler.id, g);
                          const gStatus = saveStatus[key] ?? 'idle';
                          const hasSaved = savedScores[key] !== undefined;
                          return (
                            <input
                              key={g}
                              id={`score-all-${bowler.id}-${g}`}
                              type="number"
                              min={0}
                              max={300}
                              placeholder={hasSaved ? String(savedScores[key]) : '—'}
                              value={inputValues[key] ?? ''}
                              onChange={e => handleAllGamesInput(bowler.id, g, e.target.value)}
                              onBlur={() => handleAllGamesBlur(bowler.id, g)}
                              onKeyDown={e => handleAllGamesKeyDown(e, bowler.id, g, bowlerIndex)}
                              className={`w-full text-center px-1 py-1.5 rounded-lg font-bold text-base
                                bg-gray-700 border focus:outline-none focus:border-purple-500 transition-colors
                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                ${gStatus === 'error' ? 'border-red-500 text-red-300' :
                                  gStatus === 'saved' ? 'border-green-700/50 text-green-300' :
                                  hasSaved && gStatus === 'idle' ? 'border-gray-600 text-gray-300' :
                                  'border-gray-600 text-white'}`}
                            />
                          );
                        })}
                        <div className="text-center">
                          {hasAny
                            ? <span className="text-sm font-bold text-white">{total}</span>
                            : <span className="text-gray-600 text-sm">—</span>}
                        </div>
                        <div className="flex justify-center">
                          <StatusIcon status={rowStatus} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ── Single Game Grid ───────────────────────────────── */
                <div className="space-y-1.5">
                  {/* Column header */}
                  <div className="grid grid-cols-[64px_72px_1fr_120px_32px] gap-3 px-3 pb-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Lane</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Pos</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">{t('bookie.scores.bowler', 'Bowler')}</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide text-center">{t('bookie.scores.score', 'Score')}</span>
                    <span />
                  </div>

                  {bowlers.map(bowler => {
                    const key = SCORE_KEY(bowler.id, activeGame);
                    const status = saveStatus[key] ?? 'idle';
                    const hasSaved = savedScores[key] !== undefined;

                    return (
                      <div key={bowler.id}
                        ref={el => { rowRefs.current[bowler.id] = el; }}
                        className={`grid grid-cols-[64px_72px_1fr_120px_32px] gap-3 items-center bg-gray-800 border rounded-lg px-3 py-2.5 transition-colors
                          ${bowler.id === matchedBowlerId ? 'border-purple-500 ring-1 ring-purple-500/60' : status === 'saved' ? 'border-green-700/50' : status === 'error' ? 'border-red-700/50' : 'border-gray-700'}`}>
                        {/* Lane cell */}
                        <input
                          type="number"
                          tabIndex={-1}
                          min={1}
                          placeholder="—"
                          value={laneInputs[bowler.id] ?? ''}
                          onChange={e => setLaneInputs(prev => ({ ...prev, [bowler.id]: e.target.value }))}
                          onBlur={() => saveLane(bowler.id, laneInputs[bowler.id] ?? '')}
                          className={`w-full text-center px-1 py-1.5 rounded-lg text-sm font-bold
                            bg-gray-700 border focus:outline-none focus:border-purple-500 transition-colors
                            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                            ${laneSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                              laneSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                              'border-gray-600 text-white'}`}
                        />
                        {/* Position cell */}
                        <input
                          type="number"
                          tabIndex={-1}
                          min={1}
                          max={5}
                          placeholder="—"
                          disabled={bowler.lane == null}
                          value={posInputs[bowler.id] ?? ''}
                          onChange={e => setPosInputs(prev => ({ ...prev, [bowler.id]: e.target.value }))}
                          onBlur={() => savePosition(bowler.id, posInputs[bowler.id] ?? '')}
                          className={`w-full text-center px-1 py-1.5 rounded-lg text-sm font-bold
                            bg-gray-700 border focus:outline-none transition-colors
                            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                            ${bowler.lane == null ? 'opacity-40 cursor-not-allowed border-gray-700 text-gray-500' :
                              (posInputs[bowler.id] && (parseInt(posInputs[bowler.id]) < 1 || parseInt(posInputs[bowler.id]) > 5)) ? 'border-red-500 text-red-300' :
                              posSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                              posSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                              'border-gray-600 text-white'}`}
                        />
                        <div>
                          <div className="text-white text-sm font-medium">{formatName(bowler.name)}</div>
                          <div className="text-gray-500 text-xs">{bowler.email}</div>
                        </div>
                        <div className="relative">
                          <input
                            id={`score-${bowler.id}`}
                            type="number"
                            min={0}
                            max={300}
                            placeholder={hasSaved ? String(savedScores[key]) : '—'}
                            value={inputValues[key] ?? ''}
                            onChange={e => handleInput(bowler.id, e.target.value)}
                            onBlur={() => handleBlur(bowler.id)}
                            onKeyDown={e => handleKeyDown(e, bowler.id, bowlers.indexOf(bowler))}
                            className={`w-full text-center px-2 py-1.5 rounded-lg text-white font-bold text-lg
                              bg-gray-700 border focus:outline-none focus:border-purple-500 transition-colors
                              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                              ${status === 'error' ? 'border-red-500' : 'border-gray-600'}
                              ${hasSaved && status === 'idle' ? 'text-gray-300' : 'text-white'}`}
                          />
                        </div>
                        <div className="flex justify-center">
                          <StatusIcon status={status} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quick stats row */}
              {activeGame !== 0 && bowlers.some(b => savedScores[SCORE_KEY(b.id, activeGame)] !== undefined) && (
                <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-3 gap-3 text-center">
                  {(() => {
                    const gameScores = bowlers
                      .map(b => savedScores[SCORE_KEY(b.id, activeGame)])
                      .filter(s => s !== undefined) as number[];
                    const avg = gameScores.length > 0
                      ? Math.round(gameScores.reduce((a, b) => a + b, 0) / gameScores.length)
                      : 0;
                    const high = gameScores.length > 0 ? Math.max(...gameScores) : 0;
                    const entered = gameScores.length;
                    return (
                      <>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xl font-bold text-white">{high}</div>
                          <div className="text-xs text-gray-500">{t('bookie.scores.highGame', 'High Game')}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xl font-bold text-white">{avg}</div>
                          <div className="text-xs text-gray-500">{t('bookie.scores.average', 'Average')}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xl font-bold text-white">{entered}/{bowlers.length}</div>
                          <div className="text-xs text-gray-500">{t('bookie.scores.entered', 'Entered')}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── CSV Importing Modal ──────────────────────────────────────────── */}
        {csvImporting && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }}>
            <div className="bg-gray-800 border border-gray-700 rounded-2xl px-10 py-8 flex flex-col items-center gap-4 shadow-2xl">
              <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-white font-semibold text-base">Importing scores…</p>
              <p className="text-gray-400 text-sm text-center">Matching lanes & letters and saving to the database</p>
            </div>
          </div>
        )}

        {/* ── CSV Result Summary Modal ─────────────────────────────────────── */}
        {csvSummary && !csvImporting && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }}>
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 shadow-2xl flex flex-col gap-4 min-w-[300px] max-w-sm w-full mx-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <span className="text-green-400">✓</span> CSV Import Complete
              </h3>
              <div className="flex gap-8 justify-center py-2">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{csvSummary.imported}</div>
                  <div className="text-xs text-gray-400 mt-0.5">Imported</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-500">{csvSummary.skipped}</div>
                  <div className="text-xs text-gray-400 mt-0.5">Skipped</div>
                </div>
              </div>
              {csvSummary.warnings.length > 0 && (
                <div className="rounded-lg p-3 space-y-1" style={{ background: 'rgba(161,130,0,0.12)', border: '1px solid rgba(202,138,4,0.35)' }}>
                  {csvSummary.warnings.map((w, i) => (
                    <p key={i} className="text-yellow-300 text-xs leading-snug">{w}</p>
                  ))}
                </div>
              )}
              <p className="text-gray-500 text-xs text-center">
                Skipped rows had no matching bowler assigned to that lane &amp; letter.
              </p>
              <button
                onClick={() => setCsvSummary(null)}
                className="mt-1 px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* ── SE Live Results Panel ──────────────────────────────────────── */}
        <div className={`
          w-full sm:w-80 lg:w-96 border-l border-gray-700 overflow-y-auto flex-shrink-0
          ${showResults ? 'flex flex-col' : 'hidden sm:flex sm:flex-col'}
        `} style={{ backgroundColor: '#0f1623' }}>
          <div className="sticky top-0 z-10 px-4 py-3 border-b border-gray-700 flex items-center justify-between"
            style={{ backgroundColor: '#0f1623' }}>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${loadingResults ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
              {t('bookie.scores.liveResults', 'Live Results')}
            </h3>
            <span className="text-xs text-gray-500">{seResults.length} {t('bookie.scores.events', 'events')}</span>
          </div>

          {seResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <div className="text-3xl mb-2">📊</div>
              <p className="text-gray-500 text-sm">{t('bookie.scores.noResults', 'Results will appear as scores are entered')}</p>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {seResults.map(se => {
                const gameNums = se.applies_to_game_numbers
                  ? se.applies_to_game_numbers.split(',').map(Number)
                  : [];

                // ── Render function by type ──────────────────────────────
                const renderBody = () => {
                  // SURVIVOR / ELIMINATOR — show cashers
                  if (se.type === 'survivor' || se.type === 'eliminator') {
                    const cashers: any[] = se.cashers || [];
                    const rounds: any[] = se.computed_rounds || [];
                    return (
                      <>
                        {rounds.map((r: any) => {
                          const list = se.type === 'survivor' ? r.survivor_list : r.advancer_list;
                          return (
                            <div key={r.game_number} className="px-3 py-2 border-b border-gray-700/50">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-gray-400 font-medium">Game {r.game_number}</span>
                                <span className="text-[10px] text-gray-500">
                                  {se.type === 'survivor' ? `min ${r.survivor_score}` : `top ${r.advancement_percentage}%`}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {[...(list || [])].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                                  <span key={b.bowler_id} className={`text-[10px] px-1.5 py-0.5 rounded ${se.type === 'survivor' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-orange-900/40 text-orange-300'}`}>
                                    {formatName(b.bowler_name)} ({b.score})
                                  </span>
                                ))}
                                {(list || []).length === 0 && r.pending_list?.length > 0 && (
                                  <span className="text-[10px] text-gray-600 italic">waiting…</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {cashers.length > 0 && (
                          <div className="px-3 py-2 bg-yellow-900/20">
                            <div className="text-[10px] font-bold text-yellow-300 mb-1">🏆 Cashers</div>
                            {cashers.map((c: any, i: number) => (
                              <div key={c.bowler_id} className="text-xs text-white py-0.5">
                                {i + 1}. {c.bowler_name}
                              </div>
                            ))}
                          </div>
                        )}
                        {cashers.length === 0 && rounds.length === 0 && (
                          <div className="px-4 py-3 text-xs text-gray-500 italic">Waiting for scores…</div>
                        )}
                        {cashers.length === 0 && rounds.length > 0 && (
                          <div className="px-4 py-3 text-xs text-gray-500 italic">No cashers yet — rounds in progress</div>
                        )}
                      </>
                    );
                  }

                  // MYSTERY SCORE / MYSTERY SERIES / CLEAN GAME — managed via Entries panel payout
                  if (se.type === 'mystery_score' || se.type === 'mystery_series' || se.type === 'clean_game') {
                    return (
                      <div className="px-4 py-3 text-xs text-gray-500 italic text-center">
                        Payouts managed in the Entries panel
                      </div>
                    );
                  }

                  // SIDE POTS
                  if (se.type === 'side_pots') {
                    const pots: any[] = se.pot_results || [];
                    return pots.length === 0
                      ? <div className="px-4 py-3 text-xs text-gray-500 italic">No pot configs found</div>
                      : pots.map((pot: any, pi: number) => (
                        <div key={pi} className="border-b border-gray-700/50 last:border-0">
                          <div className="px-3 py-1.5 bg-gray-750 flex items-center justify-between" style={{ backgroundColor: '#1a2235' }}>
                            <span className="text-[10px] font-semibold text-amber-300">
                              {pot.config_type === 'game_series' ? `Series G${pot.game_identifier}` : `Game ${pot.game_identifier}`}
                            </span>
                            <span className="text-[10px] text-gray-500">Top {pot.winners_pct}% → {pot.winner_count} win</span>
                          </div>
                          <div className="max-h-36 overflow-y-auto">
                            {(pot.all_entries || []).length === 0
                              ? <div className="px-3 py-2 text-[10px] text-gray-500 italic">No scores yet</div>
                              : (pot.all_entries || []).map((b: any) => (
                                <div key={b.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/30 last:border-0 ${b.rank <= pot.winner_count ? 'bg-yellow-900/15 text-yellow-200' : 'text-gray-400'}`}>
                                  <span>{b.rank}. {b.bowler_name}</span>
                                  <span className={`font-bold ${b.rank <= pot.winner_count ? 'text-yellow-300' : ''}`}>{b.score}</span>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      ));
                  }

                  // HIGH GAME / HIGH SERIES / CUSTOM — full scrollable standings
                  const withScores = se.standings.filter((s: any) => s.best_game !== null);
                  const pending = se.standings.filter((s: any) => s.best_game === null);
                  return (
                    <>
                      <div className="max-h-64 overflow-y-auto divide-y divide-gray-700/50">
                        {withScores.length === 0
                          ? <div className="px-4 py-3 text-xs text-gray-500 italic">Waiting for scores…</div>
                          : withScores.map((standing: any, idx: number) => (
                            <div key={standing.bowler_id} className="px-3 py-2 flex items-center gap-2.5">
                              <RankBadge rank={idx + 1} />
                              <div className="flex-1 min-w-0">
                                <div className="text-white text-xs font-medium truncate">{standing.bowler_name}</div>
                                {standing.game_scores.length > 1 && (
                                  <div className="text-[10px] text-gray-500 mt-0.5">
                                    {standing.game_scores.filter((gs: any) => gs.score !== null).map((gs: any) => `G${gs.game}: ${gs.score}`).join(' · ')}
                                  </div>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className={`font-bold text-sm ${idx === 0 ? 'text-yellow-400' : 'text-white'}`}>
                                  {se.type === 'high_series' ? standing.total_score : standing.best_game}
                                </div>
                                {standing.games_entered > 1 && se.type !== 'high_series' && (
                                  <div className="text-[10px] text-gray-500">{standing.total_score} tot</div>
                                )}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                      {pending.length > 0 && (
                        <div className="px-3 py-1.5 flex flex-wrap gap-1 border-t border-gray-700/50">
                          {pending.map((s: any) => (
                            <span key={s.bowler_id} className="text-[10px] text-gray-600 bg-gray-700/40 px-1.5 py-0.5 rounded">
                              {s.bowler_name.split(' ')[0]}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  );
                };

                return (
                  <div key={se.id} className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                    {/* SE Header */}
                    <div className="px-4 py-2.5 border-b border-gray-700 flex items-center justify-between"
                      style={{ backgroundColor: '#1a2235' }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{se.name}</div>
                        <div className="flex items-center gap-1.5 text-[10px] mt-0.5">
                          <span className="bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded">
                            {TYPE_LABELS[se.type] || se.type}
                          </span>
                          {gameNums.length > 0 && (
                            <span className="text-gray-500">G {gameNums.join(', ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Type-specific body */}
                    <div>
                      {renderBody()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookieScoreEntry;
